import { Country } from '@front/common/src/types/country';
import { UniverseReference } from '@front/common/src/types/commons';

import { Locale } from '@front/i18n/src/types';

import { CreditCardBrand } from '../types/credit-card';

type Config = {
  areHighlightedSlotsAvailable: boolean;
  // has the tax credit lines calculation inside the basket
  hasTaxCreditInBasket: boolean;
  isTaxInfoDisplayed: boolean;
  shouldShowInstantTaxCreditSelectionScreen: boolean;
};

export const CONFIG_BY_COUNTRY: Record<Country, Config> = {
  FR: {
    areHighlightedSlotsAvailable: true,
    hasTaxCreditInBasket: true,
    isTaxInfoDisplayed: false,
    shouldShowInstantTaxCreditSelectionScreen: true,
  },
  GB: {
    areHighlightedSlotsAvailable: false,
    hasTaxCreditInBasket: false,
    isTaxInfoDisplayed: false,
    shouldShowInstantTaxCreditSelectionScreen: false,
  },
  DE: {
    areHighlightedSlotsAvailable: false,
    hasTaxCreditInBasket: false,
    isTaxInfoDisplayed: true,
    shouldShowInstantTaxCreditSelectionScreen: false,
  },
  AT: {
    areHighlightedSlotsAvailable: false,
    hasTaxCreditInBasket: false,
    isTaxInfoDisplayed: false,
    shouldShowInstantTaxCreditSelectionScreen: false,
  },
  CH: {
    areHighlightedSlotsAvailable: false,
    hasTaxCreditInBasket: false,
    isTaxInfoDisplayed: false,
    shouldShowInstantTaxCreditSelectionScreen: false,
  },
};

export const CREDITS_CARDS_PER_COUNTRY: Record<Country, CreditCardBrand[]> = {
  FR: ['Visa', 'CB', 'Maestro', 'MasterCard', 'American Express'],
  GB: ['Visa', 'CB', 'Maestro', 'MasterCard', 'American Express'],
  DE: ['Visa', 'Maestro', 'MasterCard', 'American Express'],
  AT: ['Visa', 'Maestro', 'MasterCard', 'American Express'],
  CH: ['Visa', 'Maestro', 'MasterCard', 'American Express'],
};

export type BookingCancelCalendlyConfig =
  | string
  | undefined
  | {
      [locale in Locale]?: string;
    };

type BookingCancelCalendlyLinkByCountry = {
  wellness: null;
  childcare: null;
  cleaning: {
    spot: null;
    recurring: BookingCancelCalendlyConfig;
  };
};

export const BOOKING_CANCEL_CALENDLY_LINK_BY_COUNTRY: Record<Country, BookingCancelCalendlyLinkByCountry> = {
  FR: {
    wellness: null,
    childcare: null,
    cleaning: {
      spot: null,
      recurring: 'https://calendly.com/wecasa-cs/menage-regulier',
    },
  },
  GB: {
    wellness: null,
    childcare: null,
    cleaning: {
      spot: null,
      recurring: 'https://calendly.com/customer-success-uk/recurring-cleaning',
    },
  },
  DE: {
    wellness: null,
    childcare: null,
    cleaning: {
      spot: null,
      recurring: 'https://calendly.com/customer-success-dach-ewmp/regelmassige-reinigung',
    },
  },
  AT: {
    wellness: null,
    childcare: null,
    cleaning: {
      spot: null,
      recurring: 'https://calendly.com/customer-success-dach-ewmp/regelmassige-reinigung',
    },
  },
  CH: {
    wellness: null,
    childcare: null,
    cleaning: {
      spot: null,
      recurring: {
        'de-CH': 'https://calendly.com/customer-success-dach-ewmp/regelmassige-reinigung',
        'fr-CH': 'https://calendly.com/customer-success-dach-ewmp/menage-regulier',
      },
    },
  },
};

type MembershipCountryConfig = {
  isAvailable: boolean;
  monthlyPrice: number;
  wellnessReductionsPercentage: number;
  cleaningMaxPricePerHour: number;
};

const MEMBERSHIP_NOT_AVAILABLE_CONFIG: MembershipCountryConfig = {
  isAvailable: false,
  monthlyPrice: 0,
  wellnessReductionsPercentage: 0,
  cleaningMaxPricePerHour: 0,
};

export const MEMBERSHIP_CONFIG_BY_COUNTRY: Record<Country, MembershipCountryConfig> = {
  FR: {
    isAvailable: true,
    monthlyPrice: 990,
    wellnessReductionsPercentage: 25,
    cleaningMaxPricePerHour: 400,
  },
  GB: {
    isAvailable: true,
    monthlyPrice: 990,
    wellnessReductionsPercentage: 25,
    cleaningMaxPricePerHour: 300,
  },
  // TODO I18N: Change when/if we want to activate memberships in DACH countries
  DE: MEMBERSHIP_NOT_AVAILABLE_CONFIG,
  AT: MEMBERSHIP_NOT_AVAILABLE_CONFIG,
  CH: MEMBERSHIP_NOT_AVAILABLE_CONFIG,
};

type PayPalWebCountryConfig = {
  availableInUniverses: UniverseReference[];
};

export const PAYPAL_WEB_CONFIG_BY_COUNTRY: Record<Country, PayPalWebCountryConfig> = {
  FR: {
    availableInUniverses: ['beauty', 'haircut', 'massage', 'sports_coaching'],
  },
  GB: {
    availableInUniverses: ['beauty', 'cleaning', 'haircut', 'massage'],
  },
  DE: {
    availableInUniverses: ['cleaning'],
  },
  AT: {
    availableInUniverses: ['cleaning'],
  },
  CH: {
    availableInUniverses: ['cleaning'],
  },
};

type InstantTaxCreditPostBookingCountryConfig = {
  availableUniverses?: UniverseReference[];
};

export const INSTANT_TAX_CREDIT_CONFIG_POST_BOOKING_BY_COUNTRY: Record<
  Country,
  InstantTaxCreditPostBookingCountryConfig
> = {
  FR: {
    availableUniverses: ['cleaning', 'childcare'],
  },
  GB: {},
  DE: {},
  AT: {},
  CH: {},
};
